import type { TelemetrySummary, TelemetryVital } from '@/apis/telemetry';
import { VITAL_ORDER } from '@/pages/Shipments/partials/sensorVitals';

/** How a chart's y-axis is bounded (PRD Appendix A); `clamp` autoscales but never leaves [min, max] (D25). */
export type YAxisRule =
  | { kind: 'autoscale'; include?: readonly [number, number] }
  | { kind: 'clamp'; min: number; max: number }
  | { kind: 'fromZero' };

/** One chartable measure: its label, unit and y-axis rule. */
export interface ChartMeasure {
  key: TelemetryVital;
  label: string;
  unit: string;
  yAxis: YAxisRule;
}

/** Every chart the modal offers, in catalogue order. */
export const CHART_MEASURES: Record<TelemetryVital, ChartMeasure> = {
  temperature: {
    key: 'temperature',
    label: 'Temperature',
    unit: '°C',
    yAxis: { kind: 'autoscale', include: [0, 6] },
  },
  battery: {
    key: 'battery',
    label: 'Battery',
    unit: '%',
    yAxis: { kind: 'clamp', min: 0, max: 100 },
  },
  shock_value: {
    key: 'shock_value',
    label: 'Shock',
    unit: 'g',
    yAxis: { kind: 'fromZero' },
  },
  humidity: {
    key: 'humidity',
    label: 'Humidity',
    unit: '%RH',
    yAxis: { kind: 'autoscale' },
  },
  pressure: {
    key: 'pressure',
    label: 'Pressure',
    unit: 'psi',
    yAxis: { kind: 'autoscale' },
  },
  light: {
    key: 'light',
    label: 'Light',
    unit: 'lx',
    yAxis: { kind: 'fromZero' },
  },
  tilt: {
    key: 'tilt',
    label: 'Tilt',
    unit: '°',
    yAxis: { kind: 'fromZero' },
  },
  cell_signal: {
    key: 'cell_signal',
    label: 'Cell signal',
    unit: '%',
    yAxis: { kind: 'clamp', min: 0, max: 100 },
  },
};

/** Chart order in the stack and the picker, regardless of selection order: the vital chips' order. */
export const CHART_ORDER: readonly TelemetryVital[] = VITAL_ORDER;

/** Charts selected when a user has never saved a choice, and what Reset restores. */
export const DEFAULT_CHARTS: readonly TelemetryVital[] = [
  'temperature',
  'battery',
  'shock_value',
  'humidity',
];

/** Readings further apart than this are drawn as a break in the line (PRD §4.4). */
export const GAP_MS = 6 * 60 * 60 * 1000;

/** The given charts in catalogue order, keeping only ones that exist in `available`. */
export const orderCharts = (
  charts: readonly TelemetryVital[],
  available: readonly TelemetryVital[]
): TelemetryVital[] =>
  CHART_ORDER.filter((key) => charts.includes(key) && available.includes(key));

/** Charts at least one of the shipment's sensors has readings for, in catalogue order. */
export const availableCharts = (
  summary: TelemetrySummary | undefined
): TelemetryVital[] =>
  CHART_ORDER.filter((key) =>
    summary?.sensors.some((sensor) => key in sensor.measures)
  );

/** The saved charts this shipment has, or its first available chart when it has none of them. */
export const shownCharts = (
  saved: readonly TelemetryVital[],
  available: readonly TelemetryVital[]
): TelemetryVital[] => {
  const charts = orderCharts(saved, available);
  return charts.length > 0 || available.length === 0 ? charts : [available[0]];
};

/** The charts to save on Apply: the picker's choice for charts this shipment has, the saved choice for the rest. */
export const appliedCharts = (
  picked: readonly TelemetryVital[],
  saved: readonly TelemetryVital[],
  available: readonly TelemetryVital[]
): TelemetryVital[] =>
  CHART_ORDER.filter((key) =>
    available.includes(key) ? picked.includes(key) : saved.includes(key)
  );

/** The picker's charts after ticking or unticking one; the last ticked chart can't be unticked. */
export const toggleChart = (
  picked: readonly TelemetryVital[],
  key: TelemetryVital,
  checked: boolean,
  available: readonly TelemetryVital[]
): TelemetryVital[] => {
  if (checked) return orderCharts([...picked, key], available);
  return picked.length > 1 ? picked.filter((k) => k !== key) : [...picked];
};

/** Whether the picker must keep this chart ticked, because it's the only one ticked. */
export const isLastPicked = (
  picked: readonly TelemetryVital[],
  key: TelemetryVital
): boolean => picked.length === 1 && picked[0] === key;

/** The Charts trigger's text: "N Charts", or "All charts" on a phone when every available chart is shown (PRD Appendix B). */
export const chartsTriggerLabel = (
  shown: readonly TelemetryVital[],
  available: readonly TelemetryVital[],
  compact: boolean
): string => {
  if (compact && shown.length === available.length) return 'All charts';
  return `${shown.length} ${shown.length === 1 ? 'Chart' : 'Charts'}`;
};
